import {
  Workout,
  WorkoutDetail,
  WorkoutDetailStatus,
  WorkoutStatus,
} from 'app/@core/interfaces/common/CalorieFriend/workout';
import {
  WorkoutMode,
  WorkoutService,
} from 'app/@core/backend/common/services/CalorieFriend/workout.service';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { FitnessPlanDay } from 'app/@core/interfaces/common/CalorieFriend/plan-day';
import { OverlayFitnessItemPresentationComponent } from './../FitnessItemPresentation/fitness-item-presentation-overlay.component';
import { NbDialogService, NbDialogRef } from '@nebular/theme';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { BlockType, FitnessBlock, FitnessItem, FitnessItemHelper, SetsBased } from 'app/@core/interfaces/common/CalorieFriend/fitnessItem';
import { Utility } from 'app/@core/utils/utility';
import { WorkoutTimerService } from 'app/@core/backend/common/services/CalorieFriend/workout-timmer.service';
import * as moment from 'moment';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-fitness-workout',
  templateUrl: './fitness-workout.component.html',
  styleUrls: ['./fitness-workout.component.scss'],
})
export class FitnessWorkoutComponent implements OnInit, OnDestroy {
  constructor(
    private dialogService: NbDialogService,
    public workoutService: WorkoutService,
    private workoutTimer: WorkoutTimerService,
  ) { }

  @Input('PlanDay')
  PlanDay!: FitnessPlanDay;
  @Input('PatientID')
  PatientID!: number;
  @Input('IsPreferredUnitImperial')
  IsPreferredUnitImperial: boolean = false;
  @ViewChild('completeDlg')
  completeDlg: TemplateRef<any>;

  workoutMode: WorkoutMode;
  workout: Workout;

  _workoutSubscription: Subscription;
  _workoutModeSubscription: Subscription;
  _workoutStartExcerciseSubscription: Subscription;
  private destroy$ = new Subject<void>();

  pending = false;

  /**
   * @description Current Exercise
   */
  blockIndex: number = 0;
  itemIndex: number = 0;
  startDateTime: Date;
  setNo: number = 1;
  circuitSetNoValue: number = 1;
  time = 0;

  get currentExercise() {
    return `${String.fromCharCode(0x41 + this.blockIndex)}${this.itemIndex + 1
      }`;
  }

  get currentSetNo(): number {
      const itemsId = this.PlanDay.blocks[this.blockIndex].items.filter(item => item.setsBased !== SetsBased.Superset).map(item => item.id);
      const setNos = this.workout?.patientWorkoutDataDetails?.filter(detail => itemsId.includes(detail.itemId)).map(detail => detail.isCircuitSetNumber);
      return setNos?.length ? Math.max(...setNos) : 1;
  }

  get workoutDuration() {
    if (!this.workout) return 0;
    return Utility.getTimeDiffDisplayString(
      this.workout?.startDateTime,
      this.workout?.endDateTime,
      true
    );
  }
  public timeStringDuration;

  // get timeStringDuration() {
  //   if (!this.workout) return 0;
  //   return Utility.getTimerStringFromDates(
  //     this.workout?.startDateTime,
  //     this.workout?.endDateTime
  //   );
  // }

  /**
   * @description dialog refs
   */
  itemDialog: NbDialogRef<OverlayFitnessItemPresentationComponent>;

  ngOnInit(): void {
    this.pending = true;
    this.workoutTimer.workoutTime.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.timeStringDuration = this.workoutTimer.formatTime(res);
    });
    this._workoutModeSubscription = this.workoutTimer.workoutMode.pipe(takeUntil(this.destroy$)).subscribe(
      (mode: WorkoutMode) => {
        if (this.workoutService.dayId.value != this.PlanDay.id) return;
        this.workoutMode = mode;
      }
    );
    this.workoutService.startExcercise.pipe(takeUntil(this.destroy$)).subscribe((ev) => {
      if(ev) {
      this.workoutTimer.reset();
      if(!this.pending) this.gotToBLockExcercise(ev.excerciseId, ev.blockId, (this.workout && this.workout.status !== WorkoutStatus.None && !this.isCompleted()));
      }
    });

    this._workoutSubscription = this.workoutService.workout.subscribe(
      async (workout) => {
        /**
         * Ignore if not the current day.
         * Ignore if currently working out.
         */
        if (this.workoutService.dayId.value != this.PlanDay.id) return;

        this.pending = false;
        if(workout?.workoutTime) {
          this.time = workout.workoutTime;
          this.workoutTimer.setTime(this.time);
        } else {
        // get time difference between two dates in seconds using moment.js
        this.time = moment(workout?.startDateTime).diff(moment(this.workout?.endDateTime), 'seconds');
        }
        if(!this.workout) {
          this.workout = workout;
          const result = this.getFirstIncomplete();
          if(result) [this.blockIndex, this.itemIndex, this.setNo, this.circuitSetNoValue] = result;
        }
        this.workout = workout;
      }
    );
  }

 blockCircuitSetNo(blockIndex): number {
    const itemsId = this.PlanDay.blocks[blockIndex].items.map(item => item.id);
    const circuitSetNo = this.workout?.patientWorkoutDataDetails?.filter(detail => itemsId.includes(detail.itemId)).map(detail => detail.isCircuitSetNumber);
    return circuitSetNo?.length ? Math.max(...circuitSetNo) :1;
}

  ngOnDestroy(): void {
    this._workoutSubscription?.unsubscribe();
    this._workoutModeSubscription?.unsubscribe();
    this._workoutStartExcerciseSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * @description object for WorkoutMode Enum
   */
  readonly WorkoutMode = WorkoutMode;

  /**
   * @description called when start button clicked
   */
  onStartWorkoutClicked() {
    if (this.workoutMode == WorkoutMode.Pause) {
      this.startWorkout();

    } else if (this.workoutMode == WorkoutMode.Workout) {
      this.pause();
    }
    else {
      const confirmDlg = this.dialogService
        .open(ConfirmMessageDialogComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            Title: `Start Workout`,
            Message: $localize`:@@StartWorkoutConfirmationMessage:Are you ready to start this workout now?`,
          },
        })
        .onClose.subscribe((result) => {
          if (result){
            this.blockIndex = 0;
            this.itemIndex = 0;
            this.startWorkout();
          }
        });
    }



  }
  /**
   * @description Redo workout
   */
  redoWorkout(): void {
    this.workoutService.redoWorkout();
  }
  /**
   * @description start workout plan
   */
  startWorkout(): void {
    if (this.isCompleted()) {
      this.onCompleted();
      return;
    }

    const defaultWorkout: Workout = {
      id: this.workout?.id,
      planId: this.PlanDay.planID,
      dayId: this.PlanDay.id,
      patientId: this.PatientID,
      status: WorkoutStatus.Started,
      startDateTime: new Date(),
      endDateTime: new Date(),
    };
    this.workoutService.startWorkout(defaultWorkout);

    this.startExercise();
  }
  pause() {
    const defaultWorkout: Workout = {
      id: this.workout?.id,
      planId: this.PlanDay.planID,
      dayId: this.PlanDay.id,
      patientId: this.PatientID,
      status: WorkoutStatus.Started,
      workoutTime: this.workoutTimer.workoutTime.value,
      startDateTime: undefined,
      endDateTime: undefined
    };
    this.workoutService.pauseWorkout(defaultWorkout);
  }
  getCurrentExerciseLabel(){
    let block = this.PlanDay.blocks[this.blockIndex];
    let item = block.items[this.itemIndex];
    let currentExercise = `${String.fromCharCode(0x41 + this.blockIndex)}${this.itemIndex + 1
    }`;
    if (block.blockType == BlockType.Circuit) {
      currentExercise = `${currentExercise} - Set ${this.currentSetNo}`;
    }
    else if(FitnessItemHelper.IsSuperSet(item) || FitnessItemHelper.IsSuperSetWithNext(item, block)){
      currentExercise = `${currentExercise} - SuperSet ${this.setNo}`;
    }
    return currentExercise;
  }
  getTimeToRest(){
    let block = this.PlanDay.blocks[this.blockIndex];
    let item = block.items[this.itemIndex];
    if(block.blockType == BlockType.Block && FitnessItemHelper.IsSuperSet(item) && !FitnessItemHelper.IsSuperSetWithNext(item, block)){
      return FitnessItemHelper.GetSupersetMaster(item, block)?.defaultRest || -1;
    }
    if(block.blockType == BlockType.Circuit){
      //if last item of circuit then restEnd
      if(this.itemIndex == block.items.length - 1){
        return block.restEnd;
      }
      return block.circuitRest;
    }
    return -1; // not show rest at all
  }

  gotToBLockExcercise(itemId: number, blockId, isWorkout = true){
    let blockIndex = this.PlanDay.blocks.findIndex(x => x.id === blockId);
    if(blockIndex == -1) return;
    let itemIndex = this.PlanDay.blocks[blockIndex]?.items.findIndex(x => x.id === itemId);
    this.blockIndex = blockIndex;
    this.itemIndex = itemIndex;
    this.setNo = this.currentSetNo;
    this.workoutTimer.reset();
    this.startExercise(-1, isWorkout);
  }
  startExercise(timeToRest: number = -1, isWorkout = true) {
    let block = this.PlanDay.blocks[this.blockIndex];
    if(block.items.length == 0) this.blockIndex += 1;
    block = this.PlanDay.blocks[this.blockIndex];
    const currentExercise = this.getCurrentExerciseLabel();

    this.startDateTime = new Date();

    const nextEventHandler = async (ev: {
      status: WorkoutDetailStatus;
      exLogId?: number;
      nextIndex?: number;
    }) => {
      this.itemDialog.componentRef.instance.pending = true;
      const { status, exLogId, nextIndex } = ev;

      let block = this.PlanDay.blocks[this.blockIndex];
      let item = block?.items[this.itemIndex];

      let newDetail: WorkoutDetail = {
        blockId: block.id,
        exerciseId: item.exerciceId,
        itemId: item.id,
        startDateTime: this.startDateTime,
        endDateTime: new Date(),
        patientWorkoutDataId: this.workout.id,
        status: status,
        exerciseLogId: exLogId,
        isCircuitSetNumber: this.circuitSetNoValue,
        isSupersetNumber: this.setNo,
        exerciseName: this.getCurrentExerciseLabel(),
        customName: item.isCustom || item.isQuickAdd ? item.name : null,
      };

      await this.workoutService.updateWorkout(this.workout.id, {
        endDateTime: new Date(),
        workoutTime: this.workoutTimer.workoutTime.value
      });
      if(nextIndex == -1) {
        const newDetails = await this.workoutService.addDetail(newDetail);
        let patientWorkoutDataDetails = this.workout?.patientWorkoutDataDetails?.filter((d) => {
          return d.itemId !== newDetail.itemId;
        });
        patientWorkoutDataDetails.push(newDetail);
        this.workout = {
        ...this.workout,
        patientWorkoutDataDetails,
      };
      this.workoutService.workout.next(this.workout);
    }
      this.workoutTimer.reset();
      this.gotoNextExercise(ev.nextIndex);
    };

    this.itemDialog = this.dialogService.open(
      OverlayFitnessItemPresentationComponent,
      {
        hasScroll: false,
        closeOnBackdropClick: false,
        context: {
          OverlayItem: block.items[this.itemIndex],
          PatientID: this.PatientID,
          fitnessBlock:block,
          BlockType: block.blockType,
          CircuitRest: timeToRest,
          IsPreferredUnitImperial: this.IsPreferredUnitImperial,
          currentExercise: currentExercise,
          setNumber: this.setNo,
          isWorkout,
          onNextHandler: nextEventHandler,
          shouldDialogClose: async () => {
            const confirmDlg = this.dialogService.open(
              ConfirmMessageDialogComponent,
              {
                closeOnEsc: false,
                closeOnBackdropClick: false,
                context: {
                  Title: $localize`:@@Warning:Warning`,
                  Message:
                    'Do you want to stop workout? You can resume workout anytime until end of day today.',
                },
              }
            );
            return confirmDlg.onClose.toPromise();
          },
        },
        dialogClass: 'overlay-meal-item-dialog',
      }
    );
  }

  getExerciseFromIndex = (blockIndex: number, itemIndex: number, setNo: number) => {
    let blocks = this.PlanDay.blocks;
    let circuitSetNoValue = this.blockCircuitSetNo(blockIndex);
    let block = blocks[blockIndex];
    let detail = this.workout?.patientWorkoutDataDetails?.find(detail => detail.itemId === block.items[itemIndex].id);
    if(!detail) return [blockIndex, itemIndex, setNo, circuitSetNoValue];
    return [blockIndex, itemIndex, detail.isSupersetNumber < block.items[itemIndex].sets? detail.isSupersetNumber + 1:  detail.isSupersetNumber, circuitSetNoValue];
  };



// Returns the indices of the first incomplete exercise
getFirstIncomplete(): [number, number, number, number] | null {
  const blocks = this.PlanDay.blocks;
  const patientWorkoutDataDetails: WorkoutDetail[] = this.workout?.patientWorkoutDataDetails;
  let blockIndex = 0;
  let itemIndex = 0;
  let setNo = 1;
  let circuitSetNoValue = 1;

  while (blockIndex < blocks.length) {
    const block = blocks[blockIndex];
    const item = this.findItemNotInDetails(block, patientWorkoutDataDetails);
    const detailsOfCurrentBlock = this.getDetailsOfCurrentBlock(block, patientWorkoutDataDetails);
    const circuitSetNo = this.blockCircuitSetNo(blockIndex);
    if(FitnessItemHelper.IsSuperSet(item)) {
      console.log("Is SuperSet");
      itemIndex = block.items.indexOf(item);
      const superSetDetails = detailsOfCurrentBlock.find(detail => detail.itemId === item.id);
      if(!superSetDetails) {
        return [blockIndex, itemIndex, 1, circuitSetNo];
      }
      if(superSetDetails.isSupersetNumber < item.sets) {
        console.log("Is Circuit Set Number", superSetDetails.isSupersetNumber, item.sets);
        return [blockIndex, itemIndex, superSetDetails.isSupersetNumber + 1, circuitSetNo];
      } else if(superSetDetails.isCircuitSetNumber != circuitSetNo) {
        return [blockIndex, itemIndex, 1,  superSetDetails.isCircuitSetNumber];
      }
    }
    if (item) {
      itemIndex = block.items.indexOf(item);
      return [blockIndex, itemIndex, 1, circuitSetNo];
    }

    if (block.blockType === BlockType.Circuit) {
      const result = this.handleCircuit(block, detailsOfCurrentBlock);
      if (result !== null) {
        [itemIndex, setNo, circuitSetNoValue] = result;
        return [blockIndex, itemIndex, setNo, circuitSetNoValue];
      }
    }

    blockIndex++;
  }

  return null;
}

findItemNotInDetails(block: FitnessBlock, patientWorkoutDataDetails: WorkoutDetail[]): FitnessItem | undefined {
  return block.items.find(item => {
    const detail = patientWorkoutDataDetails?.find(detail => detail.itemId === item.id);
    const blockIndex = this.PlanDay.blocks.findIndex(x => x.id == block.id);
    const blockCircuitSetNo = this.blockCircuitSetNo(blockIndex);
    if (!detail || detail.isCircuitSetNumber != blockCircuitSetNo) {
      return true; // Item not found in patientWorkoutDataDetails
    } else if (FitnessItemHelper.IsSuperSet(item) && (detail.isSupersetNumber < item.sets || detail.isCircuitSetNumber != blockCircuitSetNo)) {
      return true; // Incomplete superset workout
    }
    return false;
  });
}

getDetailsOfCurrentBlock(block, patientWorkoutDataDetails: WorkoutDetail[]) : WorkoutDetail[]{
  return patientWorkoutDataDetails?.filter(detail => detail.blockId === block.id);
}


handleCircuit(block: FitnessBlock, detailsOfCurrentBlock: WorkoutDetail[]) {
  const isCircuitSetNumbers = detailsOfCurrentBlock.map(detail => detail.isCircuitSetNumber);
  const circuitSetNumber = (isCircuitSetNumbers.length > 0 ? Math.max(...isCircuitSetNumbers): 1) || 1;
  const leftDetail = detailsOfCurrentBlock?.find(detail => detail.isCircuitSetNumber !== circuitSetNumber);
  if (leftDetail) {
    const itemIndex = block.items.findIndex(x => x.id == leftDetail.itemId);
    return [itemIndex, 1, circuitSetNumber];
  } else if(circuitSetNumber < block.repetitionCount) {
    return [0, 1, circuitSetNumber + 1];
  }
  return null;
}

getNextExerciseForSuperset(blockIndex: number, itemIndex: number, setNo: number): [number, number, number, number] {
  const blocks = this.PlanDay.blocks;
  let block = blocks[blockIndex];
  let item = block.items[itemIndex];
  let initialItemIndex = itemIndex;
  let initialItem = item;
  let blockCircuitSetNo = this.blockCircuitSetNo(blockIndex);
  blockCircuitSetNo = blockCircuitSetNo < this.circuitSetNoValue? this.circuitSetNoValue: blockCircuitSetNo;
  let itemDetail = this.workout?.patientWorkoutDataDetails?.find(detail => detail.itemId === item.id);
  setNo = itemDetail.isSupersetNumber;
  let isBreak = false;
  while (itemIndex < block.items.length) {
    if (FitnessItemHelper.IsSuperSetWithNext(item, block)) {
      itemIndex++;
      item = block.items[itemIndex];
      let currItemDetail = this.workout?.patientWorkoutDataDetails?.find(detail => detail.itemId === item.id);
      let currentCompleted = this.checkSuperSetCompletedForWorkout(currItemDetail, item);
      if (!currentCompleted) {
        console.log("not Completed superset");
        return [blockIndex, itemIndex, currItemDetail? currItemDetail.isSupersetNumber + 1: 1, blockCircuitSetNo];
      } else if(currItemDetail?.isCircuitSetNumber != blockCircuitSetNo) {
        console.log("Completed superset but not circuit set number")
        return [blockIndex, itemIndex, 1, blockCircuitSetNo];
      }
    } else {
      console.log("Break and get next exercise");
      isBreak = true;
      break;
    }
  }
  const master = FitnessItemHelper.GetSupersetMaster(initialItem, block);
  itemDetail = this.workout?.patientWorkoutDataDetails?.find(detail => detail.itemId === initialItem.id);
  setNo = itemDetail.isSupersetNumber;
  if (master) {
    const masterDetail = this.workout?.patientWorkoutDataDetails?.find(detail => detail.itemId === master.id);
    const masterIndex = block.items.findIndex(x => x.id == master.id);
    console.log("Master Index", masterIndex, masterDetail?.isSupersetNumber, setNo);
    if(masterDetail?.isSupersetNumber >= setNo) {
      console.log("Here")
      return this.getFirstIncomplete();
    }
    if (masterDetail) {
      return [blockIndex, masterIndex, masterDetail.isSupersetNumber + 1, blockCircuitSetNo];
    } else {
      return [blockIndex, masterIndex, 1, blockCircuitSetNo];
    }
  }
  if (initialItem.sets > setNo && itemDetail.isCircuitSetNumber === blockCircuitSetNo) {
    return [blockIndex, initialItemIndex, setNo + 1, blockCircuitSetNo];
  }
  return this.getFirstIncomplete();
}
  gotoNextExercise(index:number = -1) {
    if (this.workoutMode != WorkoutMode.Workout)
      this.workoutService.startWorkout();
    if (this.isCompleted() && index==-1) {
      this.onCompleted();
      return;
    }

    const timeToRest = this.getTimeToRest();
    const currentItem = this.PlanDay.blocks[this.blockIndex].items[this.itemIndex];
    // TODO: Handle index - 1 checks first
    if(FitnessItemHelper.IsSuperSet(currentItem) && index === -1) {
      [this.blockIndex, this.itemIndex, this.setNo, this.circuitSetNoValue] = this.getNextExerciseForSuperset(this.blockIndex, this.itemIndex, this.setNo);
    } else {
      [this.blockIndex, this.itemIndex, this.setNo, this.circuitSetNoValue] = index > -1? this.getExerciseFromIndex(this.blockIndex,
        index,
        this.setNo) : this.getFirstIncomplete();
    }
    if (this.isCompleted() && index == -1) {
      this.onCompleted();
      return;
    }

    /**
     * Restart dialog, workout mode doesn't change
     */
    this.itemDialog.close(WorkoutMode.Workout);
    this.startExercise(timeToRest);
  }

  isCompleted() {
    const blocks = this.PlanDay.blocks;
    const details = this.workout?.patientWorkoutDataDetails;
    if(!details) return false;
    return blocks.every((block) =>
      block.items.every((item) => details.some(detail => detail.itemId === item.id &&
          this.checkBlockCircuitCompletedForWorkout(detail, block) && this.checkSuperSetCompletedForWorkout(detail, item)
          )
      )
    );
  }

  checkSuperSetCompletedForWorkout(detail: WorkoutDetail, item: FitnessItem) {
    if(FitnessItemHelper.IsSuperSet(item)){
      return detail?.isSupersetNumber + 1 >= item.sets;
    }
    return true;
  }

  checkBlockCircuitCompletedForWorkout(detail: WorkoutDetail, block: FitnessBlock) {
    if(block.blockType === BlockType.Circuit) {
      return detail.isCircuitSetNumber === block.repetitionCount;
    }
    return true;
  }

  onCompleted() {
    this.dialogService.open(this.completeDlg).onClose.pipe(takeUntil(this.destroy$)).subscribe(async () => {
      await this.workoutService.completeWorkout();
      this.itemDialog?.close(WorkoutMode.Complete);
    });
  }

  async stopWorkout() {
    this.dialogService
    .open(ConfirmMessageDialogComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        Title: $localize`:@@Warning:Warning`,
        Message: 'Would you like to override this workout as Completed?',
      },
    })
    .onClose.pipe(takeUntil(this.destroy$)).subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      await this.workoutService.completeWorkout();
    });
  }
}
